import { useEffect, useMemo, useRef, useState } from "react";
import {
  useWebSocket,
  WebSocketChannel,
  type WebSocketProgressUpdateMessage,
  type WebSocketProgressEntity,
  type WebSocketProgressStep,
  type WebSocketProgressScope,
} from "@huub-web/backend";
import { useUuid } from "@huub-web/ui";

type UseProgressUpdateParams = {
  entityID?: string;
  entity: WebSocketProgressEntity;
  defaultProgress?: WebSocketProgressUpdateMessage["data"]["progress"];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDataUpdate?: (data: any) => void;
  scope?: WebSocketProgressScope | WebSocketProgressScope[];
};

/**
 * Hook for listening to WebSocket progress updates for a specific entity.
 *
 * Automatically registers a progress listener when mounted and unregisters when unmounted.
 * Invokes the provided callback whenever a progress update is received for the specified entity.
 *
 * @param scope - Scope to filter updates: "data", "status", or both. Defaults to "status".
 */
export const useProgressUpdate = ({
  entityID,
  entity,
  defaultProgress = [],
  onDataUpdate,
  scope = "status",
}: UseProgressUpdateParams) => {
  const listenerUuid = useUuid();
  const { isConnected, sendMessage, registerListener, deregisterListener } =
    useWebSocket();

  const [progress, setProgress] =
    useState<WebSocketProgressStep[]>(defaultProgress);

  // Stabilize scope so that callers passing a new array literal (e.g.
  // ["status", "data"]) don't tear down the WS listener every render.
  const scopeKey = useMemo(
    () => (Array.isArray(scope) ? scope.slice().sort().join(",") : scope),
    [scope],
  );

  // Keep onDataUpdate in a ref so the listener closure always calls the
  // latest callback without the effect needing it as a dependency.
  const onDataUpdateRef = useRef(onDataUpdate);
  useEffect(() => {
    onDataUpdateRef.current = onDataUpdate;
  }, [onDataUpdate]);

  // Helper to check if a specific scope is included
  const isScopeIncluded = (targetScope: WebSocketProgressScope): boolean => {
    if (Array.isArray(scope)) {
      return scope.includes(targetScope);
    }
    return scope === targetScope;
  };

  // Register progress listener and send registration message
  useEffect(() => {
    if (!listenerUuid || !entityID || !entity || !isConnected) {
      return;
    }

    // Send registration message to backend
    sendMessage(WebSocketChannel.REGISTER_PROGRESS_LISTENER, {
      id: listenerUuid,
      entityID,
      entity,
      scope,
    });

    // Register listener for progress updates
    registerListener<WebSocketProgressUpdateMessage>(
      WebSocketChannel.PROGRESS_UPDATE,
      listenerUuid,
      (message) => {
        // Only handle updates for the specified entity and entityID
        if (
          message.data.entityID === entityID &&
          message.data.entity === entity
        ) {
          if (message.data.progress && isScopeIncluded("status")) {
            setProgress(message.data.progress);
          }
          if (message.data.data && isScopeIncluded("data")) {
            onDataUpdateRef.current?.(message.data.data);
          }
        }
      },
    );

    // Cleanup: unregister listener and send unregister message
    return () => {
      deregisterListener(WebSocketChannel.PROGRESS_UPDATE, listenerUuid);

      sendMessage(WebSocketChannel.UNREGISTER_PROGRESS_LISTENER, {
        id: listenerUuid,
      });
    };
  }, [
    listenerUuid,
    entityID,
    entity,
    sendMessage,
    registerListener,
    deregisterListener,
    scopeKey,
    isConnected,
  ]);

  return { progress };
};
